import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DashboardLayout from '../layouts/DashboardLayout';
import { listSubjects } from '../api/subjects';
import { listAssessments, updateAssessment } from '../api/assessments';
import {
  createAssessmentTemplate,
  deleteAssessmentTemplate,
  listAssessmentTemplates,
  updateAssessmentTemplate,
} from '../api/assessmentTemplates';
import { formatDateTime } from '../utils/format';
import { useSnackbar } from 'notistack';

const AdminAssessmentsPage = () => {
  const [templates, setTemplates] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [form, setForm] = useState({
    title: '',
    type: 'assessment',
    description: '',
    totalMarks: 0,
    term: '',
    grade: '',
    subjectId: '',
    lockAfterDueDate: true,
    isActive: true,
  });
  const { enqueueSnackbar } = useSnackbar();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [templateData, assessmentData, subjectData] = await Promise.all([
        listAssessmentTemplates(),
        listAssessments(),
        listSubjects(),
      ]);
      setTemplates(Array.isArray(templateData) ? templateData : []);
      setAssessments(Array.isArray(assessmentData) ? assessmentData : []);
      setSubjects(Array.isArray(subjectData) ? subjectData : []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load assessments data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = useCallback((row) => {
    if (row) {
      setForm({
        title: row.title || '',
        type: row.type || 'assessment',
        description: row.description || '',
        totalMarks: row.totalMarks || 0,
        term: row.term || '',
        grade: row.grade || '',
        subjectId: row.subjectId?._id || row.subjectId || '',
        lockAfterDueDate: row.lockAfterDueDate !== false,
        isActive: row.isActive !== false,
      });
    } else {
      setForm({
        title: '',
        type: 'assessment',
        description: '',
        totalMarks: 0,
        term: '',
        grade: '',
        subjectId: '',
        lockAfterDueDate: true,
        isActive: true,
      });
    }
  }, []);

  const handleOpenCreate = () => {
    setSelectedTemplate(null);
    resetForm(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (row) => {
    setSelectedTemplate(row);
    resetForm(row);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setDialogLoading(true);
      const payload = {
        title: form.title,
        type: form.type,
        description: form.description || null,
        totalMarks: Number(form.totalMarks) || 0,
        term: form.term,
        grade: Number(form.grade),
        subjectId: form.subjectId,
        lockAfterDueDate: Boolean(form.lockAfterDueDate),
        isActive: Boolean(form.isActive),
      };
      if (selectedTemplate?._id) {
        await updateAssessmentTemplate(selectedTemplate._id, payload);
        enqueueSnackbar('Template updated', { variant: 'success' });
      } else {
        await createAssessmentTemplate(payload);
        enqueueSnackbar('Template created', { variant: 'success' });
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to save template', { variant: 'error' });
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDelete = async (row) => {
    const confirmed = window.confirm(`Delete template "${row.title}" This cannot be undone.`);
    if (!confirmed) return;
    try {
      await deleteAssessmentTemplate(row._id);
      enqueueSnackbar('Template deleted', { variant: 'success' });
      await load();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to delete template', { variant: 'error' });
    }
  };

  const templateColumns = useMemo(() => ([
    { field: 'term', headerName: 'Term', minWidth: 120 },
    { field: 'grade', headerName: 'Grade', minWidth: 100 },
    {
      field: 'subject',
      headerName: 'Subject',
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => params?.row?.subjectId?.name || '-',
    },
    { field: 'type', headerName: 'Type', minWidth: 140 },
    { field: 'totalMarks', headerName: 'Marks', minWidth: 110 },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params?.row?.isActive ? 'Active' : 'Inactive'}
          color={params?.row?.isActive ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      minWidth: 140,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title="Edit">
            <IconButton size="small" color="primary" onClick={() => handleOpenEdit(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => handleDelete(params.row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ]), [handleDelete, handleOpenEdit]);

  const assessmentColumns = useMemo(() => ([
    { field: 'title', headerName: 'Assessment', flex: 1, minWidth: 200 },
    { field: 'type', headerName: 'Type', minWidth: 120 },
    { field: 'grade', headerName: 'Grade', minWidth: 100 },
    {
      field: 'subject',
      headerName: 'Subject',
      flex: 1,
      minWidth: 180,
      valueGetter: (params) => {
        const subject = subjects.find((s) => s._id === params?.row?.subjectId);
        return subject?.name || params?.row?.subjectId || '-';
      },
    },
    {
      field: 'moderation',
      headerName: 'Moderation',
      minWidth: 160,
      valueGetter: (params) => params?.row?.moderation?.status || 'pending',
      renderCell: (params) => (
        <Chip
          size="small"
          label={params?.row?.moderation?.status || 'pending'}
          color={params?.row?.moderation?.status === 'approved' ? 'success' : params?.row?.moderation?.status === 'rejected' ? 'error' : 'default'}
        />
      ),
    },
    {
      field: 'release',
      headerName: 'Release',
      minWidth: 140,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params?.row?.release?.released ? 'Released' : 'Locked'}
          color={params?.row?.release?.released ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      minWidth: 280,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const status = params?.row?.moderation?.status || 'pending';
        const released = params?.row?.release?.released;
        return (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              onClick={async () => {
                try {
                  await updateAssessment(params.row._id, { moderation: { status: 'approved' } });
                  enqueueSnackbar('Assessment approved', { variant: 'success' });
                  await load();
                } catch (err) {
                  enqueueSnackbar(err?.response?.data?.message || 'Failed to approve', { variant: 'error' });
                }
              }}
              disabled={status === 'approved'}
            >
              Approve
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={async () => {
                try {
                  await updateAssessment(params.row._id, { moderation: { status: 'rejected' } });
                  enqueueSnackbar('Assessment rejected', { variant: 'warning' });
                  await load();
                } catch (err) {
                  enqueueSnackbar(err?.response?.data?.message || 'Failed to reject', { variant: 'error' });
                }
              }}
              disabled={status === 'rejected'}
            >
              Reject
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={async () => {
                try {
                  await updateAssessment(params.row._id, { release: { released: true } });
                  enqueueSnackbar('Marks released', { variant: 'success' });
                  await load();
                } catch (err) {
                  enqueueSnackbar(err?.response?.data?.message || 'Failed to release marks', { variant: 'error' });
                }
              }}
              disabled={released || status !== 'approved'}
            >
              Release
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={async () => {
                try {
                  await updateAssessment(params.row._id, { release: { released: false } });
                  enqueueSnackbar('Marks locked', { variant: 'info' });
                  await load();
                } catch (err) {
                  enqueueSnackbar(err?.response?.data?.message || 'Failed to lock marks', { variant: 'error' });
                }
              }}
              disabled={!released}
            >
              Lock
            </Button>
          </Stack>
        );
      },
    },
    {
      field: 'dueDate',
      headerName: 'Due',
      minWidth: 190,
      valueGetter: (params) => (params?.row?.dueDate ? formatDateTime(params.row.dueDate) : '-'),
    },
  ]), [subjects, enqueueSnackbar, load]);
  const subjectOptions = useMemo(() => (
    form.grade ?
      subjects.filter((s) => Number(s.grade) === Number(form.grade))
      : subjects
  ), [form.grade, subjects]);

  return (
    <DashboardLayout
      title="Assessments & SBA"
      actions={(
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={load}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button startIcon={<AddIcon />} variant="contained" onClick={handleOpenCreate}>
            New Template
          </Button>
        </Stack>
      )}
    >
      <Box>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}

          <Card elevation={1} sx={{ borderRadius: 3 }}>
            <CardHeader
              title="Term Templates"
              subheader="Create Quiz/Test/Assessment templates per grade and subject."
            />
            <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
              <Box sx={{ height: 420 }}>
                <DataGrid
                  rows={templates}
                  getRowId={(row) => row._id}
                  columns={templateColumns}
                  loading={loading}
                  disableRowSelectionOnClick
                  sx={{
                    border: 'none',
                    color: 'text.primary',
                    bgcolor: 'background.paper',
                    '& .MuiDataGrid-virtualScroller': { bgcolor: 'background.paper' },
                    '& .MuiDataGrid-row': { bgcolor: 'background.paper' },
                    '& .MuiDataGrid-cell': { color: 'text.primary' },
                    '& .MuiDataGrid-cellContent': { color: 'text.primary' },
                    '& .MuiDataGrid-columnHeaders': { bgcolor: 'grey.50', color: 'text.primary' },
                    '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700, color: 'text.primary' },
                    '& .MuiDataGrid-footerContainer': { bgcolor: 'grey.50', color: 'text.primary' },
                    '& .MuiDataGrid-overlay': { color: 'text.secondary' },
                  }}
                  initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                  pageSizeOptions={[10, 25, 50]}
                />
              </Box>
            </CardContent>
          </Card>

          <Card elevation={1} sx={{ borderRadius: 3 }}>
            <CardHeader
              title="Moderation Overview"
              subheader="Educators manage moderation; view status here."
            />
            <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
              <Box sx={{ height: 360 }}>
                <DataGrid
                  rows={assessments}
                  getRowId={(row) => row._id}
                  columns={assessmentColumns}
                  loading={loading}
                  disableRowSelectionOnClick
                  sx={{
                    border: 'none',
                    color: 'text.primary',
                    bgcolor: 'background.paper',
                    '& .MuiDataGrid-virtualScroller': { bgcolor: 'background.paper' },
                    '& .MuiDataGrid-row': { bgcolor: 'background.paper' },
                    '& .MuiDataGrid-cell': { color: 'text.primary' },
                    '& .MuiDataGrid-cellContent': { color: 'text.primary' },
                    '& .MuiDataGrid-columnHeaders': { bgcolor: 'grey.50', color: 'text.primary' },
                    '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700, color: 'text.primary' },
                    '& .MuiDataGrid-footerContainer': { bgcolor: 'grey.50', color: 'text.primary' },
                    '& .MuiDataGrid-overlay': { color: 'text.secondary' },
                  }}
                  initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                  pageSizeOptions={[10, 25, 50]}
                />
              </Box>
            </CardContent>
          </Card>
        </Stack>
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
 <DialogTitle>{selectedTemplate ? 'Edit Template' : 'New Template'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="Title"
              value={form.title}
              onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
              fullWidth
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Term"
                value={form.term}
                onChange={(event) => setForm((prev) => ({ ...prev, term: event.target.value }))}
                fullWidth
              />
              <TextField
                label="Grade"
                type="number"
                value={form.grade}
                onChange={(event) => setForm((prev) => ({ ...prev, grade: event.target.value }))}
                fullWidth
              />
            </Stack>
            <TextField
              select
              label="Subject"
              value={form.subjectId}
              onChange={(event) => setForm((prev) => ({ ...prev, subjectId: event.target.value }))}
              fullWidth
            >
              {subjectOptions.map((subject) => (
                <MenuItem key={subject._id} value={subject._id}>
                  {subject.name} (G{subject.grade})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Type"
              value={form.type}
              onChange={(event) => setForm((prev) => ({ ...prev, type: event.target.value }))}
              fullWidth
            >
              <MenuItem value="quiz">Quiz</MenuItem>
              <MenuItem value="test">Test</MenuItem>
              <MenuItem value="assessment">Assessment</MenuItem>
            </TextField>
            <TextField
              label="Total marks"
              type="number"
              value={form.totalMarks}
              onChange={(event) => setForm((prev) => ({ ...prev, totalMarks: event.target.value }))}
              fullWidth
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={form.lockAfterDueDate}
                  onChange={(event) => setForm((prev) => ({ ...prev, lockAfterDueDate: event.target.checked }))}
                />
              )}
              label="Lock after due date"
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={form.isActive}
                  onChange={(event) => setForm((prev) => ({ ...prev, isActive: event.target.checked }))}
                />
              )}
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={dialogLoading}>
 {dialogLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default AdminAssessmentsPage;
